import { useCRMDataClient } from "@/services/data-client/DataClientContext";
import {
  cleanupUndefined,
  mapCustomFieldsToLead,
  structureAndCleanupData,
} from "@/services/api-data-mapping";
import { Enums, FeatureKeys } from "@/services/dropdown-enums";
import UseGql from "@/services/graph-ql/useGql";
import useSignalRClient from "@/services/signal-R/signalr-hubs";
import { ToastTemplate } from "@/services/variables";
import {
  getUsers,
  getDropdownOptionsWithoutRole,
} from "@/store/slices/general/dropdownOptionsSlice";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { leadFormMap } from "@/modules/crm/leads/constant";
import {
  Get_Lead,
  Get_Lead_Update_Fields,
  Get_Lead_Workflows,
} from "@/modules/crm/leads/graphql-queries";
import Lead_signalr from "@/modules/crm/leads/signalr-constants";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearDraftLeadEdit,
  getCustomFieldsState,
  getSocialMedias,
  setCustomFieldsGQLQueryToState,
  setCustomFieldsToState,
} from "@/store/slices/lead/leadSlice";
import { useRouter } from "next/router";
import { getAllRequests } from "@/store/slices/requests";

import OnInitHub from "@/services/signal-R/on-receive-hook";
import { convertFirstChar } from "@/services/generic.methods";
import { mappedCustomFields } from "./mapCustomField";
import { mapFormToCustomFieldsPayload } from "@/services/custom-fields/customFieldValues";
import { Get_Custom_Fields } from "@/modules/crm/leads/graphql-queries/custom-field";
import { faRectangleList } from "@fortawesome/free-solid-svg-icons/faRectangleList";
import { faBolt } from "@fortawesome/free-solid-svg-icons/faBolt";
import {
  setLeadUpdateFields,
  setLeadWorkflowsToState,
} from "@/store/slices/lead/crmLeadSlice";
import guid from "guid";
import { clearLastVisitedRoute } from "@/store/slices/route-history/routeHistorySlice";
import { getLoggedinUser } from "@/store/slices/auth/authSlice";
import useFeatures from "../useFeatures";
import { knownColsCamel } from "@/components/grid-wrapper/dynamic-columns-generator";

const buildCustomFieldsGqlFragment = (fields = []) =>
  (fields || [])
    .filter((field) => field.parentType != "MultiValue")
    .map((field) => convertFirstChar(field?.name))
    .join("\n");

let isResponseMethodSubsribed = false;
// manage lead hook

const {
  InvokeMethodNames: { assignLead, changeLeadStatus, markLeadAsSeen },
  onReceiveMethodNames: { assignLeadResponse, changeLeadStatusResponse },
} = Lead_signalr;

const getDuplicateEmailError = (conflictId) =>
  `Email already in use for Lead ID: ${conflictId}`;

const useManageLead = (props = {}) => {
  const {
    requestMethod = "",
    responseMethod = "",
    onSaveSuccess = null,
    successMsg = "",
    shouldShowToast = false,
    shouldMarkAsSeen = true,
    refetchLeadAfterAssignOrStatusId = undefined,
  } = props;
  const dispatch = useDispatch();
  const getLeadUpdateFields = UseGql(Get_Lead_Update_Fields);
  const profile = useSelector(getLoggedinUser);
  const { hasFeature } = useFeatures();
  const isCRM = hasFeature(FeatureKeys.CRM);
  const router = useRouter();
  const { query } = router;
  const users = useSelector(getUsers);
  const dropdownOptions = useSelector(getDropdownOptionsWithoutRole);
  const { hubName } = Lead_signalr;
  const { statuses, RTC } = useSignalRClient({
    hubName,
    companyKey: query?.companykey,
  });
  // Track which action this instance initiated to prevent duplicate toasts across multiple hook instances
  const pendingActionRef = useRef(null);
  const pendingOptionsRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const requests = useSelector(getAllRequests);
  const [dynamicQuery] = useState(Get_Lead);
  const customFields = useSelector(getCustomFieldsState);

  const { client: dataClient, companyKey } = useCRMDataClient();
  const { clearStateByMethods } = OnInitHub();

  const handleSubmitSingleField = (options) => {
    const leadhub = RTC[hubName];
    options?.setLoading(Enums?.RequestStatus.processing);
    // extract feild name
    const fieldName = options.saveSingleFieldName;
    // field key to be used to save
    let key = "assigneeId";
    // signalR method as per field
    let requestMethodSF = assignLead;
    let actionType = "assign";
    // override in case of a different field
    if (fieldName === "leadStatusId") {
      key = "statusId";
      requestMethodSF = changeLeadStatus;
      actionType = "changeStatus";
    }
    let value = options?.data?.[fieldName];
    if (key === "assigneeId" && value) {
      const selectedUser = users?.find((u) => u.id === value);
      if (selectedUser?.sourceId) {
        value = selectedUser.sourceId;
      }
    }
    let formData = {
      [key]: value,
      leadId: options?.data?.id,
    };

    if (statuses[hubName] && leadhub) {
      console.log("Form data for single field update::", formData);
      pendingActionRef.current = actionType;
      pendingOptionsRef.current = options;
      // send data to signlr from here
      leadhub
        .invoke(requestMethodSF, formData)
        .then((data) => {
          console.log("Single field request sent with::", data);
        })
        .catch((error) => {
          // reset button state
          options?.setLoading(Enums?.RequestStatus.done);
          pendingActionRef.current = null;
          pendingOptionsRef.current = null;
          console.log("Error::", { ...error });
          toast.error(ToastTemplate.error(error?.message || "Update failed."));
        });
    } else {
      // reset button state
      options?.setLoading(Enums?.RequestStatus.done);
    }
  };
  const handleSubmit = async (options, saveSingleFieldName) => {
    if (saveSingleFieldName) {
      // handleSubmitSingleField
    }
    options?.setLoading(Enums?.RequestStatus.processing);
    pendingActionRef.current = "submit";
    pendingOptionsRef.current = options;
    const leadhub = RTC[hubName];
    const leadhubStatus = statuses[hubName];

    // structure data as per the server's requirements
    // and remove undefined fields
    if ((customFields || []).length) {
      leadFormMap.customFields = customFields?.map((field) => field?.name);
    }
    let socialMedias = await getSocialMedias();
    let formData = structureAndCleanupData(
      options,
      leadFormMap,
      "lead",
      socialMedias?.fields
    );

    if (customFields?.length && formData?.lead) {
      formData.lead.customFields = mapFormToCustomFieldsPayload(
        options.data,
        customFields
      );
    }

    const hasEmail = !!formData?.lead?.contact?.email?.trim();
    const hasPhone = !!formData?.lead?.contact?.phone?.trim();
    const isAppointmentReturn = !!options?.requireEmailAndFirstName;

    if (!saveSingleFieldName) {
      if (isAppointmentReturn) {
        if (!hasEmail) {
          toast.error(ToastTemplate.error("Email is required."));
          options?.setLoading(Enums?.RequestStatus.failed);
          return;
        }
        if (!formData?.lead?.contact?.firstName?.trim()) {
          toast.error(ToastTemplate.error("First Name is required."));
          options?.setLoading(Enums?.RequestStatus.failed);
          return;
        }
      } else {
        if (!hasEmail && !hasPhone) {
          toast.error(ToastTemplate.error("Email or Phone is required."));
          options?.setLoading(Enums?.RequestStatus.failed);
          return;
        }
      }
    }

    customFields.forEach((field) => {
      const isNumericField =
        field?.fieldType === Enums.CustomFieldType.Numeric ||
        field?.parentType === "Numeric";
      let value = formData?.lead?.customFields?.[field?.name];
      if (
        (!value && !isNumericField) ||
        (isNumericField && (value == null || value == undefined))
      ) {
        formData.lead.customFields[field?.name] = "";
      }
    });

    if (formData?.lead) {
      if (!isCRM) {
        if (
          !formData.lead.leadSourceId ||
          String(formData.lead.leadSourceId).toLowerCase() === "none"
        ) {
          formData.lead.leadSourceId = "none";
        }
        if (
          !formData.lead.leadTypeId ||
          String(formData.lead.leadTypeId).toLowerCase() === "none"
        ) {
          formData.lead.leadTypeId = "none";
        }
      } else {
        if (
          !formData.lead.leadSourceId ||
          String(formData.lead.leadSourceId).toLowerCase() === "none"
        ) {
          formData.lead.leadSourceId = 0;
        } else if (!isNaN(Number(formData.lead.leadSourceId))) {
          formData.lead.leadSourceId = Number(formData.lead.leadSourceId);
        }

        if (
          !formData.lead.leadTypeId ||
          String(formData.lead.leadTypeId).toLowerCase() === "none"
        ) {
          formData.lead.leadTypeId = 0;
        } else if (!isNaN(Number(formData.lead.leadTypeId))) {
          formData.lead.leadTypeId = Number(formData.lead.leadTypeId);
        }
      }
    }

    if (isCRM) {
      if (formData?.lead) {
        if (formData.lead.leadOwnerId && users && users.length) {
          const matchedUser = users.find(
            (u) =>
              u.userId === formData.lead.leadOwnerId ||
              String(u.id) === String(formData.lead.leadOwnerId)
          );
          if (matchedUser) {
            formData.lead.leadOwnerId =
              matchedUser.sourceId || matchedUser.userId;
          }
        }
        if (formData.lead.assignedTo && users && users.length) {
          const matchedUser = users.find(
            (u) =>
              String(u.id) === String(formData.lead.assignedTo) ||
              u.userId === formData.lead.assignedTo
          );
          if (matchedUser) {
            formData.lead.assignedTo =
              matchedUser.sourceId || matchedUser.userId;
          }
        }
      }
    }

    if (formData?.lead?.address) {
      const addr = formData.lead.address;
      const countries = dropdownOptions?.countries || [];
      const statesProvinces = dropdownOptions?.statesProvinces || [];

      if (addr.countryId != null) {
        const matchedCountry = countries.find(
          (c) =>
            String(c.countryId) === String(addr.countryId) ||
            String(c.id) === String(addr.countryId)
        );
        addr.countryId =
          matchedCountry?.id || matchedCountry?.countryId || addr.countryId;
      }
      if (addr.stateProvinceId != null) {
        const matchedState = statesProvinces.find(
          (s) =>
            String(s.stateProvinceId) === String(addr.stateProvinceId) ||
            String(s.id) === String(addr.stateProvinceId)
        );
        addr.stateProvinceId =
          matchedState?.id ||
          matchedState?.stateProvinceId ||
          addr.stateProvinceId;
      }
    }

    const dataClientParams = {
      hub: leadhub,
      methodName: requestMethod,
      responseMethodName: responseMethod,
      ...formData,
    };

    let action = convertFirstChar(requestMethod, "lower");

    if (
      action === "addLead" &&
      dataClient?.leads &&
      !dataClient.leads.addLead &&
      dataClient.leads.createLead
    ) {
      action = "createLead";
    }

    if (action === "updateLead" && !formData.lead.leadId) {
      formData.lead.leadId = query.leadId || query.id;
    }

    pendingOptionsRef.current = {
      ...options,
      willUseDataClient: !!dataClient?.leads?.[action],
    };

    const clearPendingSubmit = () => {
      pendingActionRef.current = null;
      pendingOptionsRef.current = null;
    };

    const failBasicCreate = (message) => {
      options?.setLoading(Enums?.RequestStatus.failed);
      clearPendingSubmit();
      toast.error(ToastTemplate.error(message));
    };

    if (
      !isCRM &&
      !saveSingleFieldName &&
      action === "createLead" &&
      typeof dataClient?.leads?.validateLeadField === "function"
    ) {
      const email = formData?.lead?.contact?.email?.trim();
      if (email) {
        try {
          const existingLead = await dataClient.leads.validateLeadField(
            "email",
            email,
            { companyKey }
          );
          const normalizedEmail = email.toLowerCase();
          const isDuplicate =
            existingLead &&
            String(existingLead.email || "")
              .trim()
              .toLowerCase() === normalizedEmail;
          if (isDuplicate) {
            const conflictId = existingLead?.id || existingLead?.leadId;
            failBasicCreate(getDuplicateEmailError(conflictId));
            return;
          }
        } catch (error) {
          console.error("[useManageLead] duplicate email check:", error);
        }
      }
    }

    if (dataClient?.leads?.[action]) {
      dataClient.leads[action](dataClientParams, { companyKey })
        .then((response) => {
          let lead = response?.lead ?? response;
          const leadId = lead?.id || lead?.leadId;
          if (lead && options?.data) {
            const contact = lead.contact || {};
            lead = {
              ...lead,
              ...(leadId && { id: leadId, leadId }),
              firstName:
                lead.firstName ||
                contact.firstName ||
                options.data.firstName ||
                "",
              lastName:
                lead.lastName ||
                contact.lastName ||
                options.data.lastName ||
                "",
              email: lead.email || contact.email || options.data.email || "",
              phone: lead.phone || contact.phone || options.data.phone || "",
              countryCode:
                lead.countryCode ||
                contact.countryCode ||
                options.data.countryCode ||
                "",
            };
          }
          const isBasicCreate = !isCRM && action === "createLead";

          if (isBasicCreate && !lead?.id && !lead?.leadId) {
            failBasicCreate("Lead could not be created.");
            return;
          }

          options?.setLoading(Enums?.RequestStatus.done);
          clearPendingSubmit();

          if (lead && lead.assignedTo && users && users.length) {
            const matchedUser = users.find(
              (u) =>
                Number(u.sourceId) === Number(lead.assignedTo) ||
                u.id === lead.assignedTo ||
                u.userId === lead.assignedTo
            );
            if (matchedUser) {
              lead = { ...lead, assignedTo: matchedUser.id };
            }
          }
          if (onSaveSuccess) {
            onSaveSuccess(lead);
          }
          if (options?.leadDraftKey && options?.visitedRouteKey) {
            dispatch(clearDraftLeadEdit({ leadId: options.leadDraftKey }));
            dispatch(
              clearLastVisitedRoute({ routeKey: options.visitedRouteKey })
            );
          }
          if (shouldShowToast && successMsg) {
            toast.success(ToastTemplate.success(successMsg));
          }
          if (options.redirect) {
            router.push(`/crm/leads`);
          }
        })
        .catch((error) => {
          options?.setLoading(Enums?.RequestStatus.done);
          clearPendingSubmit();
          console.error(`[useManageLead] ${requestMethod} error:`, error);
          toast.error(ToastTemplate.error(error.message || "Operation failed"));
        });
    } else {
      // reset button state
      options?.setLoading(Enums?.RequestStatus.done);
    }
  };

  // Handle create/update responses - only for the instance that initiated the request
  useEffect(() => {
    if (!responseMethod) return;
    if (pendingActionRef.current !== "submit") return;
    const response = requests[responseMethod];
    if (!response) return;

    const opts = pendingOptionsRef.current;
    if (opts?.willUseDataClient) {
      if (!response.isSuccess) {
        opts?.setLoading?.(Enums?.RequestStatus.done);
        clearStateByMethods([responseMethod]);
        pendingActionRef.current = null;
        pendingOptionsRef.current = null;
      } else {
        clearStateByMethods([responseMethod]);
      }
      return;
    }

    if (response.isSuccess) {
      let lead = response.lead;
      if (lead && lead.assignedTo && users && users.length) {
        const matchedUser = users.find(
          (u) =>
            Number(u.sourceId) === Number(lead.assignedTo) ||
            u.id === lead.assignedTo ||
            u.userId === lead.assignedTo
        );
        if (matchedUser) {
          lead = { ...lead, assignedTo: matchedUser.id };
        }
      }
      if (onSaveSuccess) {
        onSaveSuccess(lead);
      }
      console.log("Record added successfully");
      if (shouldShowToast && successMsg) {
        toast.success(ToastTemplate.success(successMsg));
      }
      opts?.setLoading?.(Enums?.RequestStatus.done);
      if (opts?.leadDraftKey && opts?.visitedRouteKey) {
        dispatch(clearDraftLeadEdit({ leadId: `${opts.leadDraftKey}` }));
        dispatch(
          clearLastVisitedRoute({ routeKey: `${opts.visitedRouteKey}` })
        );
      }
      if (opts?.redirect) {
        router.push(`/crm/leads`);
      }
    } else if (response?.errors?.length) {
      opts?.setLoading?.(Enums?.RequestStatus.done);
    } else {
      opts?.setLoading?.(Enums?.RequestStatus.done);
    }
    clearStateByMethods([responseMethod]);
    pendingActionRef.current = null;
    pendingOptionsRef.current = null;
  }, [requests[responseMethod]]);

  // Handle assign/change-status responses - only for the instance that initiated the request
  useEffect(() => {
    const action = pendingActionRef.current;
    if (action !== "assign" && action !== "changeStatus") return;
    const responseKey =
      action === "assign" ? assignLeadResponse : changeLeadStatusResponse;
    const response = requests[responseKey];
    if (!response) return;

    const opts = pendingOptionsRef.current;
    if (response.isSuccess) {
      if (shouldShowToast && successMsg) {
        toast.success(ToastTemplate.success(successMsg));
      }
    } else if (response?.errors?.length) {
      if (shouldShowToast) {
        toast.error(ToastTemplate.error(response?.errors?.join(" ")));
      }
    }
    opts?.setLoading?.(Enums?.RequestStatus.done);
    if (response.isSuccess && opts?.redirect) {
      router.push(`/crm/leads`);
    }
    setTimeout(() => {
      clearStateByMethods([responseKey]);
      pendingActionRef.current = null;
      pendingOptionsRef.current = null;
    }, 0);
  }, [requests[assignLeadResponse], requests[changeLeadStatusResponse]]);

  // Sync lead data when assign/change-status succeeds (for sibling components like LeadInfoTab)
  useEffect(() => {
    if (!onSaveSuccess || refetchLeadAfterAssignOrStatusId == null) return;
    const leadId = +refetchLeadAfterAssignOrStatusId;
    const keys = [assignLeadResponse, changeLeadStatusResponse];
    for (const key of keys) {
      const response = requests[key];
      if (!response?.isSuccess) continue;
      const rid = response.lead?.id ?? response.leadId;
      if (rid != null && +rid !== leadId) continue;
      onSaveSuccess(response.lead);
      break;
    }
  }, [requests[assignLeadResponse], requests[changeLeadStatusResponse]]);

  // get lead from server to populate the form
  const getLead = useCallback(
    async ({
      setResponse,
      removeNull = false,
      leadId,
      customFields: customFieldsOverride,
    }) => {
      const fieldsForMapping =
        customFieldsOverride?.length > 0 ? customFieldsOverride : customFields;
      setIsLoading(true);
      try {
        const idToFetch = !isCRM
          ? query.id || leadId
          : leadId || query.leadId || query.id;
        return dataClient.leads
          .getLeadDetails(idToFetch, {
            query: dynamicQuery,
            companyKey: query?.companykey,
          })
          .then((data) => {
            if (Object.keys(data?.leadDetails || {}).length > 0) {
              let lead = mapCustomFieldsToLead(
                { ...data?.leadDetails[0] },
                fieldsForMapping
              );
              lead = cleanupUndefined(lead);
              if (lead?.leadTags) {
                let tagsoptions = knownColsCamel.tagIds();
                lead["tags"] = tagsoptions.filter((tag) =>
                  lead.leadTags.map((tag) => tag.tagId).includes(tag.id)
                );
              }
              if (lead["salutation"]) {
                lead["salutation"] = lead["salutation"].replace(".", "").trim();
              }
              lead = addPlusBeforeCountryCode(lead);

              // Map assignedTo sourceId to userId/id UUID string
              if (lead.assignedTo && users && users.length) {
                const matchedUser = users.find(
                  (u) =>
                    Number(u.sourceId) === Number(lead.assignedTo) ||
                    u.id === lead.assignedTo ||
                    u.userId === lead.assignedTo
                );
                if (matchedUser) {
                  lead.assignedTo = matchedUser.id;
                }
              }

              // Ensure stateProvinceId is initialized for dropdown form selection
              if (
                lead.stateProvinceId != null &&
                dropdownOptions?.statesProvinces?.length
              ) {
                const matchedState = dropdownOptions.statesProvinces.find(
                  (s) =>
                    String(s.id) === String(lead.stateProvinceId) ||
                    String(s.stateProvinceId) === String(lead.stateProvinceId)
                );
                if (matchedState) {
                  lead.stateProvinceId =
                    matchedState.stateProvinceId || matchedState.id;
                }
              }

              // set api response, this will populate the form with saved data
              setResponse(lead);
              setNotFound(false);
              if (shouldMarkAsSeen) {
                handleMarkLeadAsSeen(lead);
              }
            } else {
              setResponse(null);
              setNotFound(true);
            }
            setIsLoading(false);
          });
      } catch (error) {
        toast.error(ToastTemplate.error(error || error.message));
        setIsLoading(false);
      }
    },
    [
      dynamicQuery,
      users,
      customFields,
      dataClient,
      companyKey,
      query,
      isCRM,
      shouldMarkAsSeen,
      dropdownOptions,
    ]
  );

  const handleMarkLeadAsSeen = (lead) => {
    const leadhub = RTC[hubName];
    if (lead?.isNew && statuses[hubName] && leadhub) {
      //Note: call signalr method to mark lead as isNew false in BE
      let payload = {
        id: lead?.id,
        requestId: guid.raw(),
      };
      leadhub
        .invoke(markLeadAsSeen, payload)
        .then((data) => {
          console.log("Request sent markLeadAsSeen::", data);
        })
        .catch((error) => {
          console.log("Error::", { ...error });
        });
    }
  };

  const addPlusBeforeCountryCode = (lead) => {
    ["alternateCountryCode", "countryCode"].forEach((code) => {
      if (lead?.[code] && !lead?.[code]?.includes("+")) {
        lead[code] = `+${lead[code]}`;
      }
    });
    return lead;
  };

  const [isCustomFieldsFetching, setIsCustomFieldsFetching] = useState(true);
  let getCustomFieldsGQL = UseGql(Get_Custom_Fields);

  const getCustomFields = async () => {
    setIsCustomFieldsFetching(true);

    try {
      return getCustomFieldsGQL
        .executeQuery({ skip: 0, take: 100 })
        .then((response) => {
          const { data } = response;

          setIsCustomFieldsFetching(false);
          const fields = data?.customFields?.items;
          if (fields?.length) {
            const mappedFields = mappedCustomFields(fields);
            dispatch(setCustomFieldsToState(mappedFields));
            dispatch(setCustomFieldsToState(mappedFields));

            let customFieldsQuery = buildCustomFieldsGqlFragment(mappedFields);

            dispatch(setCustomFieldsGQLQueryToState(customFieldsQuery));
            return mappedFields;
          } else {
            dispatch(setCustomFieldsToState([]));
            return [];
          }
        })
        .catch((error) => {
          setIsCustomFieldsFetching(false);
          throw error;
        });
    } catch (error) {
      setIsCustomFieldsFetching(false);
    }
  };
  const fetchLeadUpdateFields = () => {
    getLeadUpdateFields.executeQuery().then((res) => {
      let { data } = res;
      if (data?.leadUpdateFields?.items) {
        let fields = data?.leadUpdateFields?.items;
        dispatch(setLeadUpdateFields(fields));
      }
    });
  };

  const [leadWorkflows, setLeadWorkflows] = useState([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  let getWorkflowsGQL = UseGql(Get_Lead_Workflows);

  const getLeadWorkflows = async (leadId) => {
    if (!leadId) {
      console.log("Lead ID is required");
      return;
    }
    setWorkflowsLoading(true);
    dispatch(setLeadWorkflowsToState([]));
    try {
      return getWorkflowsGQL
        .executeQuery({ leadId: +leadId, skip: 0, take: 100 })
        .then((response) => {
          const { data } = response;
          if (data?.leadWorkflows?.items?.length > 0) {
            const workflows = filteredWorkflows(
              data?.leadWorkflows?.items,
              data?.leadWorkflows.totalCount
            );
            // set api response, this will populate the form with saved data
            dispatch(setLeadWorkflowsToState(workflows));
            setLeadWorkflows(workflows);
          } else {
            setLeadWorkflows([]);
            dispatch(setLeadWorkflowsToState([]));
          }
          setWorkflowsLoading(false);
        });
    } catch (error) {
      toast.error(ToastTemplate.error(error || error.message));
      setWorkflowsLoading(false);
    }
  };

  const filteredWorkflows = (workflows, count) => {
    const activeWorkflows = workflows
      .filter(
        (workflow) =>
          workflow.workflowRunStatus != Enums.WorkflowStatus.Completed
      )
      .map(mapWorkflows);
    const completedWorkflows = workflows
      .filter(
        (workflow) =>
          workflow.workflowRunStatus == Enums.WorkflowStatus.Completed
      )
      .map(mapWorkflows);
    return {
      count: count || workflows.length,
      activeWorkflows,
      completedWorkflows,
    };
  };

  const mapWorkflows = (wf) => {
    const workflow = JSON.parse(JSON.stringify(wf));
    workflow.icon = faRectangleList;
    if (workflow.triggerIds?.length > 0) {
      workflow.icon = faBolt;
    }
    switch (workflow.leadRunStatus) {
      case Enums.WorkflowLeadRunStatus.NotStarted:
        workflow.status = {
          label: "Pending",
          styles: {
            backgroundColor: "var(--palette-warning_bg)",
            color: "var(--palette-warning)",
          },
        };
        break;

      case Enums.WorkflowLeadRunStatus.Running:
        workflow.status = {
          label: "Running",
          styles: {
            backgroundColor: "var(--palette-active_bg)",
            color: "var(--palette-active)",
          },
        };
        break;
      case Enums.WorkflowLeadRunStatus.Completed:
        workflow.status = {
          label: "Completed",
          styles: {
            backgroundColor: "var(--palette-success_bg)",
            color: "var(--palette-success)",
          },
        };
        break;
      case Enums.WorkflowLeadRunStatus.Terminated:
        workflow.status = {
          label: "Terminated",
          styles: {
            backgroundColor: "var(--palette-error_bg)",
            color: "var(--palette-error)",
          },
        };
        break;
      case Enums.WorkflowLeadRunStatus.OptedOut:
        workflow.status = {
          label: "OptedOut",
          styles: {
            backgroundColor: "var(--palette-error_bg)",
            color: "var(--palette-warning)",
          },
        };
        break;
      default:
        workflow.status = {};
        break;
    }
    return workflow;
  };

  return {
    handleSubmit,
    handleSubmitSingleField,
    getLead,
    isLoading,
    getCustomFields,
    fetchLeadUpdateFields,
    isCustomFieldsFetching,
    customFields,
    leadWorkflows,
    workflowsLoading,
    getLeadWorkflows,
    notFound,
  };
};

export default useManageLead;
